import { ReactElement, useState, useEffect, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import throttle from 'lodash/throttle';
import { useRouter } from 'next/router';
import { useSwipeable } from 'react-swipeable';

import { bugsnag } from 'shared/utils/services/bugsnag';
import { Caret } from 'shared/icons';
import { customCache } from 'homepage/utils/HomeTabCustomCache';
import {
  DESKTOP_BANNER_HEIGHT_NUMERIC,
  MOBILE_BANNER_HEIGHT_NUMERIC,
} from 'banner/enums/constants';
import {
  DESKTOP_HEADER_HEIGHT_NUMERIC,
  NEW_NAV_MOBILE_HEADER_HEIGHT_NUMERIC,
} from 'layout/header/enums/constants';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { PageLayoutActions } from 'layout/state/pageLayoutReducer';
import { variableFont, variableFontRegular } from 'styles/theme';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { setActiveTab, setVisitedByBackButton } from 'store/actions/homepageActions';
import {
  TAB_INITIAL_HEIGHT,
  TAB_INITIAL_HEIGHT_MOBILE,
  TAB_OFFSET,
  TAB_HIDDEN_PIXELS,
} from 'homepage/styles/constants';
import {
  useBanner,
  useBodyScrollLock,
  usePageLayoutDispatch,
  usePageLayoutState,
  useWindowSize,
} from 'shared/hooks';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';

interface IHomeTab {
  children?: ReactElement;
  tabColor?: string;
  tabPosition?: number;
  textColor?: string;
  title: string;
  noTopPadding?: boolean;
}

export const HomeTab = ({
  children,
  tabColor,
  tabPosition = 0,
  textColor,
  title,
  noTopPadding = false,
}: IHomeTab) => {
  const { activeTab, openTabScrollPosition, visitedByBackButton } = useStoreSelector(
    (state) => state.homepage,
  );
  const { isHeaderVisible } = usePageLayoutState();
  const { isBannerEnabled, title: bannerTitle, subtitle } = useBanner();
  const { isMobile } = useWindowSize();
  const dispatch = usePageLayoutDispatch();
  const storeDispatch = useStoreDispatch();
  const router = useRouter();
  const tabRef = useRef<HTMLDivElement>();
  const isTabOpen = activeTab === title;
  const tabIsOpenButNotThisTab = activeTab && activeTab !== title && tabPosition !== 0;
  const titleForId = title?.toLowerCase()?.replace(/[^\dA-Za-z]/g, '-');

  const [initialLoad, setIsInitialLoad] = useState(!activeTab);
  const [hideTab, setHideTab] = useState(false);
  const [tabHeight, setTabHeight] = useState(64);
  const [tabScrollPosition, setTabScrollPosition] = useState(0);

  useBodyScrollLock(isTabOpen, tabRef);

  function getPosition() {
    const pageHeight = window.innerHeight;
    setTabHeight(pageHeight);
  }

  const handleTabToggle = () => {
    if (!isTabOpen) {
      getPosition();
      storeDispatch(setActiveTab(title));
      setIsInitialLoad(false);
      window.scrollTo(0, 0);
      dispatch({ type: PageLayoutActions.setIsFooterVisible, payload: false });

      sendTrackingEvent('DRAWER_EXPAND', {
        drawer_name: title?.toLowerCase(),
      });
    }
  };

  const handleClose = () => {
    if (isTabOpen) {
      storeDispatch(setActiveTab(null));
      dispatch({ type: PageLayoutActions.setIsFooterVisible, payload: true });
      dispatch({ type: PageLayoutActions.setIsHeaderVisible, payload: true });
    }
  };

  const getHeaderHeight = () => {
    const heights = { desktopHeaderHeight: 0, mobileHeaderHeight: 0 };
    const isBannerVisible = isBannerEnabled && (bannerTitle || subtitle);

    if (isHeaderVisible) {
      heights.desktopHeaderHeight += DESKTOP_HEADER_HEIGHT_NUMERIC;
      heights.mobileHeaderHeight += NEW_NAV_MOBILE_HEADER_HEIGHT_NUMERIC;

      if (isBannerVisible) {
        heights.desktopHeaderHeight += DESKTOP_BANNER_HEIGHT_NUMERIC;
        heights.mobileHeaderHeight += MOBILE_BANNER_HEIGHT_NUMERIC;
      }
    }

    return heights;
  };

  const handlers = useSwipeable({
    onSwipedUp: handleTabToggle,
    onSwipedDown: handleClose,
  });

  const headerHandlers = useSwipeable({
    onSwipedDown: handleClose,
  });

  const { desktopHeaderHeight: desktop, mobileHeaderHeight: mobile } = getHeaderHeight();
  const [desktopHeaderHeight, setDesktopHeaderHeight] = useState(desktop);
  const [mobileHeaderHeight, setMobileHeaderHeight] = useState(mobile);

  useEffect(() => {
    window.addEventListener('scroll', getPosition, { passive: true });
    window.addEventListener('resize', getPosition, { passive: true });
    return () => {
      window.removeEventListener('scroll', getPosition);
      window.removeEventListener('resize', getPosition);
    };
  }, []);

  useEffect(() => {
    const currentTabContent = document.querySelector(`#tab-content-${titleForId}`);

    if (isMobile) {
      dispatch({ type: PageLayoutActions.setIsHeaderVisible, payload: true });
    }

    const handleScrollingInTab = () => {
      if (!isMobile) {
        if (
          currentTabContent?.scrollTop <= tabScrollPosition ||
          currentTabContent?.scrollTop === 0
        ) {
          dispatch({ type: PageLayoutActions.setIsHeaderVisible, payload: true });
        } else {
          dispatch({ type: PageLayoutActions.setIsHeaderVisible, payload: false });
        }
      }
      setTabScrollPosition(currentTabContent?.scrollTop);
    };

    const throttledScrollHandler = throttle(handleScrollingInTab, 0);

    currentTabContent?.addEventListener('scroll', throttledScrollHandler, { passive: true });
    return () => {
      currentTabContent?.removeEventListener('scroll', throttledScrollHandler);
    };
  }, [isHeaderVisible, isMobile]);

  useEffect(() => {
    if (!isHeaderVisible && !isTabOpen && !activeTab) {
      setHideTab(true);
    } else {
      setHideTab(false);
    }

    const { desktopHeaderHeight: desk, mobileHeaderHeight: mobl } = getHeaderHeight();
    setDesktopHeaderHeight(desk);
    setMobileHeaderHeight(mobl);
  }, [isHeaderVisible, isTabOpen, activeTab]);

  useEffect(() => {
    const handleRouteChange = (url) => {
      try {
        if (isTabOpen) {
          const currentTabContent = document.querySelector(`#tab-content-${titleForId}`);
          customCache.write(
            { tabName: title, scrollPosition: currentTabContent?.scrollTop },
            title,
          );

          if (url !== '/') {
            storeDispatch(setActiveTab(null));
            dispatch({ type: PageLayoutActions.setIsFooterVisible, payload: true });
            dispatch({ type: PageLayoutActions.setIsHeaderVisible, payload: true });
          }
        }
      } catch (error) {
        bugsnag(error, { context: 'routeChangeStart on Homepage Tab' });
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [activeTab, isTabOpen]);

  useEffect(() => {
    if (isTabOpen) {
      getPosition();
      setIsInitialLoad(false);

      const currentTabContent = document.querySelector(`#tab-content-${titleForId}`);
      if (currentTabContent && visitedByBackButton) {
        currentTabContent.scrollTop = openTabScrollPosition;
        storeDispatch(setVisitedByBackButton(false));
        dispatch({ type: PageLayoutActions.setIsHeaderVisible, payload: true });
      }
    }
  }, [activeTab]);

  return (
    <HomeTab.Container
      data-qa="homepage_tab"
      $desktopHeaderHeight={desktopHeaderHeight}
      $height={tabHeight}
      id="homepage-tab"
      $isOpen={isTabOpen}
      $initialLoad={initialLoad}
      $hideTab={hideTab}
      $tabPosition={tabPosition}
      $lowerTab={tabIsOpenButNotThisTab}
      $mobileHeaderHeight={mobileHeaderHeight}
      onClick={handleTabToggle}
      key={title}
      {...handlers}
    >
      {!isTabOpen && <HomeTab.DragRadius onClick={handleTabToggle} {...handlers} />}
      <HomeTab.HeaderContainer
        $initialLoad={initialLoad}
        $isOpen={isTabOpen}
        onClick={handleClose}
        $tabColor={tabColor}
        $textColor={textColor}
        {...headerHandlers}
      >
        <HomeTab.Title data-qa="homepage_tab_title" $textColor={textColor}>
          {title}
          <HomeTab.CaretWrapper>
            <Caret
              color={textColor || 'white'}
              direction={isTabOpen ? 'down' : 'up'}
              data-qa="homepage_tab_close_btn"
              size={8}
            />
          </HomeTab.CaretWrapper>
        </HomeTab.Title>
        {(isTabOpen && !tabColor) ||
          (tabColor === 'black' && (
            <HomeTab.Gradient
              $desktopHeaderHeight={desktopHeaderHeight}
              $mobileHeaderHeight={mobileHeaderHeight}
            />
          ))}
      </HomeTab.HeaderContainer>
      <HomeTab.ContentContainer
        id={`tab-content-${titleForId}`}
        $isOpen={isTabOpen}
        $noTopPadding={noTopPadding}
        ref={tabRef}
        $tabColor={tabColor}
      >
        {children}
      </HomeTab.ContentContainer>
    </HomeTab.Container>
  );
};

const slideTab = keyframes`
  from { top: 44px; }
  to { top: 0px; }
`;

HomeTab.Container = styled.div<{
  $desktopHeaderHeight: number;
  $height: number;
  $hideTab: boolean;
  $isOpen: boolean;
  $initialLoad: boolean;
  $lowerTab: boolean;
  $mobileHeaderHeight: number;
  $tabPosition: number;
}>`
  ${({ $isOpen, $lowerTab, $tabPosition }) =>
    !$isOpen && !$lowerTab && `bottom: ${$tabPosition * 50 - 2}px;`}
  ${({ $isOpen, $lowerTab, $tabPosition }) =>
    !$isOpen && $lowerTab && `bottom: ${($tabPosition - 1) * 50 - 2}px;`}
  ${({ $height, $isOpen, $mobileHeaderHeight }) =>
    $isOpen
      ? `max-height: ${$height - $mobileHeaderHeight - 16}px; height: ${
          $height - $mobileHeaderHeight - 16
        }px;`
      : `max-height: ${TAB_INITIAL_HEIGHT_MOBILE as string}; height: ${
          TAB_INITIAL_HEIGHT_MOBILE as string
        }; cursor: pointer;`}

  align-items: center;
  background-color: ${({ theme, $isOpen }) => $isOpen && theme.colors.black};
  bottom: -2px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: fixed;
  width: 100%;
  z-index: ${({ theme, $isOpen }) =>
    $isOpen ? theme.zIndex.homepageTabsActive : theme.zIndex.homepageTabsInactive};

  ${({ $isOpen, $tabPosition }) => !$isOpen && `bottom: ${$tabPosition * 50 - 2}px;`}
  ${({ $isOpen, $lowerTab, $tabPosition }) =>
    !$isOpen && $lowerTab && `bottom: ${($tabPosition - 1) * 50 - 2}px;`}

  ${hideScrollbars}

  transition: all 0.3s ease-in;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${({ $desktopHeaderHeight, $height, $isOpen }) =>
      $isOpen
        ? `max-height:  ${$height - $desktopHeaderHeight - 24}px; height: ${
            $height - $desktopHeaderHeight - 24
          }px;`
        : `max-height: ${TAB_INITIAL_HEIGHT}; height: ${TAB_INITIAL_HEIGHT};`}
  }

  ${({ $hideTab }) => $hideTab && `bottom: -${TAB_HIDDEN_PIXELS};`}
`;

HomeTab.DragRadius = styled.div`
  background: none;
  height: 63px;
  width: 100vw;
  position: fixed;
  bottom: 0;
  z-index: ${({ theme }) => theme.zIndex.nav};
`;

HomeTab.HeaderContainer = styled.div<{
  $initialLoad: boolean;
  $isOpen: boolean;
  $tabColor: string;
  $textColor: string;
}>`
  align-items: flex-start;
  ${({ $initialLoad, $isOpen }) =>
    $initialLoad && !$isOpen
      ? css`
          animation: ${slideTab} 0.5s forwards 2s 1;
        `
      : `top: 0px !important;`}
  background-color: ${({ $tabColor, theme }) => $tabColor || theme.colors.black};
  border-top: ${({ $textColor, theme }) =>
    $textColor ? `1px solid ${$textColor}` : theme.borders.opaqueWhite};
  cursor: pointer;
  display: flex;
  height: ${TAB_INITIAL_HEIGHT_MOBILE};
  justify-content: space-between;
  left: 16px;
  padding-top: 8px;
  position: absolute;
  top: 50px;
  width: calc(100% - 16px - 24px);
  z-index: ${({ theme }) => theme.zIndex.homepageTabHeader};

  ${hideScrollbars}

  &::before {
    background-color: ${({ $tabColor }) => $tabColor || 'none'};
    border-left: ${({ $textColor, theme }) =>
      $textColor ? `1px solid ${$textColor}` : theme.borders.opaqueWhite};
    content: '';
    height: 70px;
    left: 0px;
    position: absolute;
    top: -1px;
    transform: rotate(44deg);
    transform-origin: top left;
    width: 42px;
  }

  &::after {
    background-color: ${({ $tabColor }) => $tabColor || 'none'};
    border-right: ${({ $textColor, theme }) =>
      $textColor ? ` 1px solid ${$textColor}` : theme.borders.opaqueWhite};
    content: '';
    height: 70px;
    position: absolute;
    right: 0px;
    top: -1px;
    transform: rotate(-50.2deg);
    transform-origin: top right;
    width: 48px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    height: ${TAB_INITIAL_HEIGHT};
    width: calc(100% - 16px - 32px);

    &::before {
      transform: rotate(19deg);
    }

    &::after {
      transform: rotate(-34deg);
    }
  }
`;

HomeTab.MobileDragIndicator = styled.span`
  height: 1px;
  border-top: 1px solid ${({ theme }) => theme.colors.white};
  width: 25px;
  position: absolute;
  left: 0;
  right: 0;
  top: 8px;
  margin: auto;
  z-index: ${({ theme }) => theme.zIndex.homepageTabBorderCropper};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    display: none;
  }
`;

HomeTab.CaretWrapper = styled.div`
  display: flex;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin-right: 10px;
  }
`;

HomeTab.Title = styled.h1<{ $textColor: string }>`
  position: relative;
  top: 8px;
  padding-left: 4px;
  text-transform: uppercase;
  width: 100%;
  ${variableFont({ size: '11.5px', height: '14px', spacing: '0', fontWeight: '600' })};
  z-index: ${({ theme }) => theme.zIndex.homepageTabBorderCropper};
  display: flex;
  justify-content: space-between;

  ${({ $textColor }) => $textColor && `color: ${$textColor};`}

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    padding-left: 16px;
    top: 8px;
  }
`;

HomeTab.CloseButton = styled.button`
  border: none;
  display: none;
  height: 12px;
  margin-right: 16px;
  outline: none;
  position: absolute;
  right: 0px;
  ${variableFontRegular({ size: '8px', height: '12px' })};
  z-index: ${({ theme }) => theme.zIndex.homepageTabButton};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    display: block;
  }
`;

HomeTab.Gradient = styled.div<{ $desktopHeaderHeight: number; $mobileHeaderHeight: number }>`
  position: absolute;
  height: 36px;
  background-image: linear-gradient(to top, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.9));
  left: -20px;
  min-width: calc(100vw + 20px);
  right: 0;
  top: calc(${TAB_INITIAL_HEIGHT} - ${TAB_OFFSET});
  width: 100%;
  z-index: ${({ theme }) => theme.zIndex.homepageGradient};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    height: 48px;
    top: calc(${TAB_INITIAL_HEIGHT} - ${TAB_OFFSET});
  }
`;

HomeTab.ContentContainer = styled.div<{
  $isOpen: boolean;
  $noTopPadding: boolean;
  $tabColor: string;
}>`
  top: 50px;
  max-height: calc(100% - 44px);
  padding-top: ${({ $noTopPadding }) => ($noTopPadding ? '0px' : '50px')};
  overflow: scroll;
  position: absolute;
  width: 100%;
  background-color: ${({ $tabColor, theme }) => $tabColor || theme.colors.black};
  z-index: ${({ theme, $tabColor }) =>
    $tabColor !== 'black' || !$tabColor
      ? theme.zIndex.homepageTabHeader
      : theme.zIndex.homepageContent};
  min-height: 100vh;

  ${hideScrollbars}

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    top: calc(${TAB_INITIAL_HEIGHT} - 1px);
  }
`;
